import { useState, useEffect } from "react";
import { doc, setDoc, deleteDoc, collection, getDocs, query, where } from "firebase/firestore";
import getFirebase from "../../utilities/firebase";
import { Timestamp } from "firebase/firestore";
import { createLogger } from "../../utilities/logger";

const log = createLogger("UploadMockDemoDataTool");

const COMPONENT_CLASS = "c-upload-mock-demo-data-tool";

interface UploadResult {
    collection: string;
    docId: string;
    success: boolean;
    error?: string;
}

interface ExportedData {
    users?: any[];
    wellnessPrograms?: any[];
    mskScores?: any[];
    correctivePrograms?: any[];
    focusArticles?: any[];
}

interface NextIds {
    users: number;
    wellnessPrograms: number;
    mskScores: number;
    correctivePrograms: number;
    focusArticles: number;
}

const reconstructTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => reconstructTimestamps(item));
    }
    if (typeof obj === "object") {
        if (obj.seconds !== undefined && obj.nanoseconds !== undefined) {
            return new Timestamp(obj.seconds, obj.nanoseconds);
        }
        const result: any = {};
        for (const key of Object.keys(obj)) {
            result[key] = reconstructTimestamps(obj[key]);
        }
        return result;
    }
    return obj;
};

const getMaxIdNumber = (docs: any[], prefix: string): number => {
    let max = 0;
    for (const d of docs) {
        const id = d.id;
        if (id.startsWith(prefix + "-")) {
            const num = parseInt(id.replace(prefix + "-", ""), 10);
            if (!isNaN(num) && num > max) {
                max = num;
            }
        }
    }
    return max;
};

const UploadMockDemoDataTool: React.FC = () => {
    const [results, setResults] = useState<UploadResult[]>([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [dataCounts, setDataCounts] = useState({ users: 0, wellnessPrograms: 0, mskScores: 0, correctivePrograms: 0, focusArticles: 0 });
    const [importedData, setImportedData] = useState<ExportedData | null>(null);
    const [demoOrgs, setDemoOrgs] = useState<{ id: string; name: string }[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string>("");
    const [orgsLoading, setOrgsLoading] = useState(false);
    const [deleteExisting, setDeleteExisting] = useState(false);

    const ALLOWED_PROJECT_ID = "symmio-app-stage";
    const currentProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
    const isAllowed = currentProjectId === ALLOWED_PROJECT_ID;

    useEffect(() => {
        const fetchDemoOrgs = async () => {
            setOrgsLoading(true);
            const { db } = getFirebase();
            try {
                const q = query(collection(db, "organizations"), where("isDemo", "==", true));
                const snap = await getDocs(q);
                const orgs = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
                setDemoOrgs(orgs);
                if (orgs.length > 0) {
                    setSelectedOrgId(orgs[0].id);
                }
            } catch (e) {
                log.error("Error fetching demo orgs", {
                    exception: e,
                });
            }
            setOrgsLoading(false);
        };
        fetchDemoOrgs();
    }, []);

    const doDeleteExisting = async (orgId: string): Promise<void> => {
        const { db } = getFirebase();

        // Get all demo users (by organizationId)
        const usersQuery = query(collection(db, "users"), where("organizationId", "==", orgId));
        const usersSnap = await getDocs(usersQuery);
        const demoUserIds = usersSnap.docs.map(d => d.id);

        // Delete demo users
        for (const userDoc of usersSnap.docs) {
            await deleteDoc(doc(db, "users", userDoc.id));
        }

        // Delete wellness programs for demo users
        for (const userId of demoUserIds) {
            const wpQuery = query(collection(db, "wellnessPrograms"), where("userId", "==", userId));
            const wpSnap = await getDocs(wpQuery);
            for (const wpDoc of wpSnap.docs) {
                await deleteDoc(doc(db, "wellnessPrograms", wpDoc.id));
            }
        }

        // Delete MSK scores for demo users
        for (const userId of demoUserIds) {
            const mskQuery = query(collection(db, "mskScores"), where("userId", "==", userId));
            const mskSnap = await getDocs(mskQuery);
            for (const mskDoc of mskSnap.docs) {
                await deleteDoc(doc(db, "mskScores", mskDoc.id));
            }
        }

        // Delete corrective programs for demo users
        for (const userId of demoUserIds) {
            const cpQuery = query(collection(db, "userCorrectivePrograms"), where("userId", "==", userId));
            const cpSnap = await getDocs(cpQuery);
            for (const cpDoc of cpSnap.docs) {
                await deleteDoc(doc(db, "userCorrectivePrograms", cpDoc.id));
            }
        }

        // Delete focus articles for demo users
        for (const userId of demoUserIds) {
            const faQuery = query(collection(db, "userMskFocusArticles"), where("userId", "==", userId));
            const faSnap = await getDocs(faQuery);
            for (const faDoc of faSnap.docs) {
                await deleteDoc(doc(db, "userMskFocusArticles", faDoc.id));
            }
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(true);
        try {
            const content = await file.text();
            const parsed = JSON.parse(content);
            const data: ExportedData = {
                users: (parsed.users || []).map(reconstructTimestamps),
                wellnessPrograms: (parsed.wellnessPrograms || []).map(reconstructTimestamps),
                mskScores: (parsed.mskScores || []).map(reconstructTimestamps),
                correctivePrograms: (parsed.correctivePrograms || []).map(reconstructTimestamps),
                focusArticles: (parsed.focusArticles || []).map(reconstructTimestamps)
            };
            setImportedData(data);
            setDataCounts({
                users: data.users?.length || 0,
                wellnessPrograms: data.wellnessPrograms?.length || 0,
                mskScores: data.mskScores?.length || 0,
                correctivePrograms: data.correctivePrograms?.length || 0,
                focusArticles: data.focusArticles?.length || 0
            });
            setDataLoaded(true);
        } catch (err) {
            log.error("Error loading JSON", {
                exception: err,
            });
            alert("Invalid JSON file");
        }
        setLoading(false);
    };

    const resetForm = () => {
        setImportedData(null);
        setDataLoaded(false);
        setDataCounts({ users: 0, wellnessPrograms: 0, mskScores: 0, correctivePrograms: 0, focusArticles: 0 });
        setResults([]);
    };

    const uploadImportedData = async () => {
        if (!importedData || !selectedOrgId) return;

        if (!isAllowed) {
            alert(`Upload blocked! This tool only works on staging (current project: ${currentProjectId})`);
            return;
        }

        setUploading(true);
        setResults([]);
        const { db } = getFirebase();

        // Delete existing data if checkbox is checked
        if (deleteExisting) {
            setResults([{ collection: "", docId: "delete", success: true, error: "Deleting existing data..." }]);
            try {
                await doDeleteExisting(selectedOrgId);
                setResults([{ collection: "", docId: "delete", success: true, error: "Deleted existing data" }]);
            } catch (e: any) {
                setResults([{ collection: "", docId: "delete", success: false, error: `Delete failed: ${e.message}` }]);
                setUploading(false);
                return;
            }
        }

        const nextIds: NextIds = { users: 0, wellnessPrograms: 0, mskScores: 0, correctivePrograms: 0, focusArticles: 0 };

        if (!deleteExisting) {
            try {
                const allUsersSnap = await getDocs(collection(db, "users"));
                nextIds.users = getMaxIdNumber(allUsersSnap.docs, "demo-user");

                const wpDocs = await getDocs(collection(db, "wellnessPrograms"));
                nextIds.wellnessPrograms = getMaxIdNumber(wpDocs.docs, "demo-wellness-program");

                const mskDocs = await getDocs(collection(db, "mskScores"));
                nextIds.mskScores = getMaxIdNumber(mskDocs.docs, "demo-msk-score");

                const cpDocs = await getDocs(collection(db, "userCorrectivePrograms"));
                nextIds.correctivePrograms = getMaxIdNumber(cpDocs.docs, "demo-corrective-program");

                const faDocs = await getDocs(collection(db, "userMskFocusArticles"));
                nextIds.focusArticles = getMaxIdNumber(faDocs.docs, "demo-focus-articles");

                setResults([{ collection: "", docId: "ids", success: true, error: `Starting IDs: users ${nextIds.users + 1}, WPs ${nextIds.wellnessPrograms + 1}` }]);

            } catch (e: any) {
                setResults([{ collection: "", docId: "ids", success: false, error: `Error finding IDs: ${e.message}` }]);
                setUploading(false);
                return;
            }
        } else {
            setResults([{ collection: "", docId: "ids", success: true, error: "Starting IDs from 1 (delete mode)" }]);
        }

        const newResults: UploadResult[] = [];

        try {
            // Build user ID mapping
            const userIdMap: { [oldId: string]: string } = {};
            importedData.users?.forEach((u, i) => {
                userIdMap[u.id] = `demo-user-${nextIds.users + i + 1}`;
            });

            const wpIdMap: { [oldId: string]: string } = {};

            // Upload wellness programs first (to build mapping)
            if (importedData.wellnessPrograms) {
                for (let i = 0; i < importedData.wellnessPrograms.length; i++) {
                    const wp = importedData.wellnessPrograms[i];
                    const newId = `demo-wellness-program-${nextIds.wellnessPrograms + i + 1}`;
                    const newUserId = userIdMap[wp.userId] || wp.userId;
                    wpIdMap[wp.id] = newId;
                    try {
                        await setDoc(doc(db, "wellnessPrograms", newId), { ...wp, id: newId, userId: newUserId });
                        newResults.push({ collection: "wellnessPrograms", docId: newId, success: true });
                    } catch (e: any) {
                        newResults.push({ collection: "wellnessPrograms", docId: newId, success: false, error: e.message });
                    }
                }
            }

            // Upload users (now with remapped wellnessProgramId)
            if (importedData.users) {
                for (let i = 0; i < importedData.users.length; i++) {
                    const user = importedData.users[i];
                    const newId = `demo-user-${nextIds.users + i + 1}`;
                    const newWpId = user.wellnessProgramId ? (wpIdMap[user.wellnessProgramId] || user.wellnessProgramId) : undefined;
                    try {
                        await setDoc(doc(db, "users", newId), { ...user, id: newId, wellnessProgramId: newWpId, organizationId: selectedOrgId });
                        newResults.push({ collection: "users", docId: newId, success: true });
                    } catch (e: any) {
                        newResults.push({ collection: "users", docId: newId, success: false, error: e.message });
                    }
                }
            }

            // Upload MSK scores
            if (importedData.mskScores) {
                let mskIdx = 0;
                for (const msk of importedData.mskScores) {
                    const newId = `demo-msk-score-${nextIds.mskScores + mskIdx + 1}`;
                    const newUserId = userIdMap[msk.userId] || msk.userId;
                    try {
                        await setDoc(doc(db, "mskScores", newId), { ...msk, id: newId, userId: newUserId });
                        newResults.push({ collection: "mskScores", docId: newId, success: true });
                    } catch (e: any) {
                        newResults.push({ collection: "mskScores", docId: newId, success: false, error: e.message });
                    }
                    mskIdx++;
                }
            }

            // Upload corrective programs
            if (importedData.correctivePrograms) {
                for (let i = 0; i < importedData.correctivePrograms.length; i++) {
                    const cp = importedData.correctivePrograms[i];
                    const newId = `demo-corrective-program-${nextIds.correctivePrograms + i + 1}`;
                    const newUserId = userIdMap[cp.userId] || cp.userId;
                    try {
                        await setDoc(doc(db, "userCorrectivePrograms", newId), { ...cp, id: newId, userId: newUserId });
                        newResults.push({ collection: "userCorrectivePrograms", docId: newId, success: true });
                    } catch (e: any) {
                        newResults.push({ collection: "userCorrectivePrograms", docId: newId, success: false, error: e.message });
                    }
                }
            }

            // Upload focus articles
            if (importedData.focusArticles) {
                for (let i = 0; i < importedData.focusArticles.length; i++) {
                    const fa = importedData.focusArticles[i];
                    const newId = `demo-focus-articles-${nextIds.focusArticles + i + 1}`;
                    const newUserId = userIdMap[fa.userId] || fa.userId;
                    try {
                        await setDoc(doc(db, "userMskFocusArticles", newId), { ...fa, id: newId, userId: newUserId });
                        newResults.push({ collection: "userMskFocusArticles", docId: newId, success: true });
                    } catch (e: any) {
                        newResults.push({ collection: "userMskFocusArticles", docId: newId, success: false, error: e.message });
                    }
                }
            }

        } catch (error: any) {
            log.error("Upload error", {
                exception: error,
                organizationId: selectedOrgId,
            });
        }

        setResults(newResults);
        setUploading(false);
    };

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    return (
        <div className={COMPONENT_CLASS} style={{ maxWidth: 800 }}>
            <h3>Upload Demo Data</h3>
            <div style={{ marginBottom: 16, padding: 8, backgroundColor: isAllowed ? "#d4edda" : "#f8d7da", borderRadius: 4 }}>
                <strong>Current project:</strong> {currentProjectId || "not set"}
                {!isAllowed && <span style={{ color: "red", marginLeft: 8 }}>(Upload blocked - staging only)</span>}
            </div>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>Demo Organization:</label>
                {orgsLoading ? (
                    <span>Loading organizations...</span>
                ) : (
                    <select
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                        style={{ padding: 8, width: 300 }}
                    >
                        {demoOrgs.map(org => (
                            <option key={org.id} value={org.id}>{org.name} ({org.id})</option>
                        ))}
                    </select>
                )}
            </div>

            <div style={{ marginBottom: 16 }}>
                <label>
                    <input
                        type="checkbox"
                        checked={deleteExisting}
                        onChange={(e) => setDeleteExisting(e.target.checked)}
                        style={{ marginRight: 8 }}
                    />
                    Delete all existing data for this organization before uploading
                </label>
            </div>

            <p style={{ marginBottom: 16 }}>
                Select a JSON file exported from the Export tool. IDs are auto-incremented from existing data.
            </p>

            <div style={{ marginBottom: 16 }}>
                <input
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    disabled={loading || dataLoaded}
                    style={{ padding: 8 }}
                />
            </div>

            {dataLoaded && (
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                    <strong>Data loaded:</strong>
                    <ul style={{ margin: "8px 0 0 0" }}>
                        <li>{dataCounts.users} users</li>
                        <li>{dataCounts.wellnessPrograms} wellness programs</li>
                        <li>{dataCounts.mskScores} MSK scores</li>
                        <li>{dataCounts.correctivePrograms} corrective programs</li>
                        <li>{dataCounts.focusArticles} focus articles</li>
                    </ul>
                </div>
            )}

            {dataLoaded && (
                <button
                    onClick={resetForm}
                    disabled={uploading}
                    style={{ padding: "10px 20px", marginLeft: 12, cursor: uploading ? "not-allowed" : "pointer" }}
                >
                    Choose Different File
                </button>
            )}

            <button
                onClick={uploadImportedData}
                disabled={uploading || !dataLoaded}
                style={{ padding: "10px 20px", cursor: uploading || !dataLoaded ? "not-allowed" : "pointer" }}
            >
                {uploading ? "Uploading..." : "Upload to Firestore"}
            </button>

            {results.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <h4>Results: {successCount} succeeded, {failCount} failed</h4>
                    <div style={{
                        maxHeight: 300,
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        padding: 10,
                        fontSize: 12,
                        fontFamily: "monospace"
                    }}>
                        {results.map((result, index) => (
                            <div
                                key={index}
                                style={{
                                    color: result.success ? "green" : "red",
                                    marginBottom: 4
                                }}
                            >
                                {result.collection}/{result.docId}: {result.success ? "SUCCESS" : `FAILED - ${result.error}`}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadMockDemoDataTool;